import { ComponentType } from 'react';
import { useLocation } from 'react-router';
import { Tabs, Tab } from '@mui/material';
import { CircleCheck } from 'lucide-react';

import { FooterLogo } from '@labradorsports/assets';
import { capitalize, useSite } from '@labradorsports/utils';

import { HelmetWrapper, useNavigateWithQuery } from '../../shared/index.js';
import { AccountManagementForm } from '../index.js';

interface OwnProps {
    FormComponent: ComponentType<any>;
}

const LoginPage: FC<OwnProps> = ({ FormComponent }) => {
    const { Icons, Config } = useSite();
    const navigate = useNavigateWithQuery();
    const location = useLocation();

    const tab = location.pathname.split('/').pop();

    const formChange = (evt: any, form: string) => {
        navigate(`/${form}`);
    };

    const description = `Sign in or create your ${Config.SiteConfig.SiteName} account to access powerful tools for ${Config.SiteConfig.Sport} coaches. Share and design plays with our virtual playbook, organize teams, and track player performance.`;

    return (
        <div className="login-page">
            <HelmetWrapper description={description} />
            <div className="login-page-content">
                <Icons.FullLogo className="site-logo mb-2" />
                <div className="login-page-features-container">
                    <h2>Welcome to {Config.SiteConfig.SiteName}</h2>
                    <p>The ultimate team management platform for {Config.SiteConfig.Sport} coaches and players.</p>

                    <ul className="login-page-features">
                        <li>
                            <CircleCheck />
                            <div>
                                <h3>{capitalize(Config.SiteConfig.Sport)} Play Designer</h3>
                                <p>
                                    Build custom {Config.SiteConfig.Sport} plays with our Virtual Play Designer. Start
                                    with 10 plays, free!
                                </p>
                            </div>
                        </li>

                        <li>
                            <CircleCheck />
                            <div>
                                <h3> Organize Your Team</h3>
                                <p>Manage rosters, schedules, and team communication in one place.</p>
                            </div>
                        </li>

                        <li>
                            <CircleCheck />
                            <div>
                                <h3>Plan to Win</h3>
                                <p>Create practices, scout opponents, and evaluate players—like a pro.</p>
                            </div>
                        </li>
                    </ul>
                </div>

                <div className="login-page-footer small">
                    <FooterLogo className="labsports-logo" />© {new Date().getFullYear()} Labrador Sports, LLC. All
                    rights reserved.
                </div>
            </div>
            <div className="login-page-form">
                <Icons.FullLogo className="d-lg-none site-logo mb-2" />
                <div className="login-form-content">
                    <h2> Get Started</h2>
                    <p>Sign into your account or create a new one.</p>
                    <Tabs value={tab} onChange={formChange} variant="fullWidth">
                        <Tab value="login" label="Login" />
                        <Tab value="signup" label="Signup" />
                    </Tabs>
                    <AccountManagementForm FormComponent={FormComponent} />
                    <div className="d-lg-none login-page-footer small">
                        <FooterLogo className="labsports-logo" />
                        <span>© {new Date().getFullYear()} Labrador Sports, LLC. All rights reserved.</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
